// Updated water footprint calculator functions

export const calculateWaterFootprint = (formData) => {
  const indoor = calculateIndoorUsage(
    formData.indoor,
    formData.personal?.household || 1
  );
  const outdoor = calculateOutdoorUsage(formData.outdoor);

  const totalAnnualLiters = indoor.annualLiters + outdoor.annualLiters;
  const totalDailyLitersPerPerson =
    indoor.dailyLitersPerPerson +
    outdoor.annualLiters / 365 / (formData.personal?.household || 1);

  return {
    indoor,
    outdoor,
    totalAnnualLiters,
    totalDailyLitersPerPerson,
    indoorPercentage: (indoor.annualLiters / totalAnnualLiters) * 100,
    outdoorPercentage: (outdoor.annualLiters / totalAnnualLiters) * 100,
  };
};

export const calculateIndoorUsage = (indoor, householdSize) => {
  // Default values if data is missing
  householdSize = householdSize || 1;

  // Calculate bathing water usage based on method
  let bathingLiters = 0;
  // Only include bathing in calculation if the user has interacted with this section
  if (indoor?.bathingMethodUpdated) {
    if (indoor?.bathingMethod === "shower") {
      // Shower: frequency per week * duration * liters per minute * 52 weeks per year
      const showerFlow = indoor?.efficientShowerhead ? 7.5 : 10; // liters per minute
      bathingLiters =
        (indoor?.showerFrequency || 7) *
        (indoor?.showerDuration || 5) *
        showerFlow *
        52;
    } else if (indoor?.bathingMethod === "bucket") {
      // Bucket: 25 liters per bath * frequency per week * 52 weeks per year
      bathingLiters = 25 * (indoor?.bucketBathFrequency || 7) * 52;
    } else if (indoor?.bathingMethod === "bathtub") {
      // Bathtub: 300 liters per bath * frequency per month * 12 months per year
      bathingLiters = 300 * (indoor?.bathtubFrequency || 4) * 12;
    }
  }

  // Calculate toilet water usage
  let toiletLiters = 0;
  // Only include toilet usage if the user has interacted with this section
  if (indoor?.toiletTypeUpdated || indoor?.toiletFlushesUpdated) {
    // Only count toilet usage if it's not using treated water
    if (indoor?.usesTreatedWater !== true) {
      const flushesPerDay = indoor?.toiletFlushes || 5;
      const flushesPerYear = flushesPerDay * 365;

      // Calculate based on toilet type
      if (indoor?.toiletType === "traditional") {
        // Traditional: 10 liters per flush
        toiletLiters = 10 * flushesPerYear;
      } else if (indoor?.toiletType === "dualFlush") {
        // Dual flush: (6L * flushes)/2 + (3L * flushes)/2
        toiletLiters = (6 * flushesPerYear) / 2 + (3 * flushesPerYear) / 2;
      } else if (indoor?.toiletType === "efficientDual") {
        // Efficient dual flush: (4L * flushes)/2 + (2L * flushes)/2
        toiletLiters = (4 * flushesPerYear) / 2 + (2 * flushesPerYear) / 2;
      } else {
        // Default to traditional if not specified
        toiletLiters = 10 * flushesPerYear;
      }
    }
  }

  // Calculate faucet water usage
  let faucetLiters = 0;
  // Only include faucet usage if the user has interacted with this section
  if (indoor?.faucetMinutesUpdated || indoor?.efficientFaucetsUpdated) {
    const faucetFlow = indoor?.efficientFaucets ? 4 : 6; // liters per minute
    const faucetMinutesPerDay = indoor?.faucetMinutes || 10;
    faucetLiters = faucetMinutesPerDay * faucetFlow * 365;
  }

  // Calculate dishwashing water usage
  let dishwashingLiters = 0;
  // Only include dishwashing if the user has interacted with this section
  if (indoor?.dishwashingMethodUpdated) {
    if (indoor?.dishwashingMethod === "dishwasher") {
      // Dishwasher: frequency per day * 15 liters per cycle * 365 days / household size
      dishwashingLiters =
        ((indoor?.dishwasherFrequency || 1) * 15 * 365) / householdSize;
    } else if (indoor?.dishwashingMethod === "handwash") {
      // Handwashing: minutes per day * 6 liters per minute * 365 days / household size
      dishwashingLiters =
        ((indoor?.dishwashingMinutes || 15) * 6 * 365) / householdSize;
    }
  }

  // Calculate laundry water usage
  let laundryLiters = 0;
  // Only include laundry if the user has interacted with this section
  if (indoor?.laundryMethodUpdated) {
    if (indoor?.laundryMethod === "machine") {
      // Determine liters per wash based on machine type
      let litersPerWash = 120; // Default to semi-automatic
      if (indoor?.washerType === "frontLoad") {
        litersPerWash = 60;
      } else if (indoor?.washerType === "topLoad") {
        litersPerWash = 140;
      } else if (indoor?.washerType === "semiAuto") {
        litersPerWash = 120;
      }

      // Full loads efficiency factor (reduce by 20% if not running full loads)
      const fullLoadFactor = indoor?.fullLoads === false ? 1.2 : 1;

      // Machine: loads per week * liters per wash * 52 weeks / household size * efficiency factor
      laundryLiters =
        (((indoor?.laundryLoads || 3) * litersPerWash * 52) / householdSize) *
        fullLoadFactor;
    } else if (indoor?.laundryMethod === "handwash") {
      // Handwash: buckets per session * 20 liters per bucket * sessions per week * 52 weeks / household size
      laundryLiters =
        ((indoor?.laundryBuckets || 2) *
          20 *
          (indoor?.handwashingFrequency || 2) *
          52) /
        householdSize;
    }
  }

  // Sum all indoor water usage
  const annualLiters =
    bathingLiters +
    toiletLiters +
    faucetLiters +
    dishwashingLiters +
    laundryLiters;

  return {
    annualLiters,
    dailyLitersPerPerson: annualLiters / 365,
    details: {
      bathing: bathingLiters,
      toilet: toiletLiters,
      faucet: faucetLiters,
      dishwashing: dishwashingLiters,
      laundry: laundryLiters,
    },
    userInteracted: {
      bathing: indoor?.bathingMethodUpdated || false,
      toilet:
        indoor?.toiletTypeUpdated || indoor?.toiletFlushesUpdated || false,
      faucet:
        indoor?.faucetMinutesUpdated ||
        indoor?.efficientFaucetsUpdated ||
        false,
      dishwashing: indoor?.dishwashingMethodUpdated || false,
      laundry: indoor?.laundryMethodUpdated || false,
    },
  };
};

export const calculateOutdoorUsage = (outdoor) => {
  let annualLiters = 0;
  const details = {};

  // Garden water usage
  if (outdoor?.hasGarden) {
    let gardenLiters = 0;

    // Based on garden size and watering frequency
    const gardenSqFt = outdoor?.gardenSqFt || 100;
    const gardenWateringFrequency = outdoor?.gardenWateringFrequency || 3;

    // Calculate liters per watering based on method
    let litersPerSqFt = 0;
    if (outdoor?.gardenWateringMethod === "hose") {
      litersPerSqFt = 0.5;
    } else if (outdoor?.gardenWateringMethod === "sprinkler") {
      litersPerSqFt = 0.4;
    } else if (outdoor?.gardenWateringMethod === "drip") {
      litersPerSqFt = 0.25;
    } else if (outdoor?.gardenWateringMethod === "rainwater") {
      litersPerSqFt = 0; // No municipal water used
    } else {
      litersPerSqFt = 0.45; // Default if method not specified
    }

    // Calculate based on plant type
    const plantFactor =
      outdoor?.plantType === "waterIntensive"
        ? 1.2
        : outdoor?.plantType === "mixed"
        ? 1.0
        : outdoor?.plantType === "waterEfficient"
        ? 0.7
        : outdoor?.plantType === "xeriscape"
        ? 0.3
        : 1.0;

    // Calculate weekly liters
    const weeklyLiters =
      gardenSqFt * litersPerSqFt * plantFactor * gardenWateringFrequency;

    // Assume 30 weeks of watering per year (adjusting for seasonal variations)
    gardenLiters = weeklyLiters * 30;

    annualLiters += gardenLiters;
    details.garden = gardenLiters;
  }

  // Lawn water usage
  if (outdoor?.hasLawn) {
    let lawnLiters = 0;

    const lawnSqFt = outdoor?.lawnSqFt || 1000;
    const lawnWateringFrequency = outdoor?.lawnWateringFrequency || 2;

    // Calculate liters per square foot per watering based on method
    let litersPerSqFt = 0;
    if (outdoor?.lawnWateringMethod === "hose") {
      litersPerSqFt = 0.6;
    } else if (outdoor?.lawnWateringMethod === "automatic") {
      litersPerSqFt = 0.5;
    } else if (outdoor?.lawnWateringMethod === "smart") {
      litersPerSqFt = 0.35;
    } else {
      litersPerSqFt = 0.5; // Default
    }

    // Apply efficiency factor based on time of day
    const timeFactor =
      outdoor?.lawnWateringTime === "midday"
        ? 1.3
        : outdoor?.lawnWateringTime === "morning"
        ? 0.9
        : outdoor?.lawnWateringTime === "evening"
        ? 1.0
        : 1.0;

    // Calculate weekly liters
    const weeklyLiters =
      lawnSqFt * litersPerSqFt * lawnWateringFrequency * timeFactor;

    // Assume 30 weeks of watering per year
    lawnLiters = weeklyLiters * 30;

    annualLiters += lawnLiters;
    details.lawn = lawnLiters;
  }

  // Car washing calculations
  if (outdoor?.carWashMethod && outdoor?.carWashMethod !== "never") {
    let carWashLiters = 0;
    const washesPerMonth = outdoor?.carWashFrequency || 1;

    switch (outdoor.carWashMethod) {
      case "hoseBasic":
        // Basic hose: 400L per wash
        carWashLiters = 400 * washesPerMonth * 12;
        break;
      case "hoseNozzle":
        // Hose with nozzle: 150L per wash
        carWashLiters = 150 * washesPerMonth * 12;
        break;
      case "bucket":
        // Bucket method: 20L per bucket
        const bucketsPerWash = outdoor?.carWashBuckets || 2;
        carWashLiters = 20 * bucketsPerWash * washesPerMonth * 12;
        break;
      case "carWash":
        // Commercial car wash: 120L per wash
        carWashLiters = 120 * washesPerMonth * 12;
        break;
      default:
        carWashLiters = 0;
    }

    annualLiters += carWashLiters;
    details.carWash = carWashLiters;
  }

  return {
    annualLiters,
    monthlyLiters: annualLiters / 12,
    details,
  };
};

export const generateRecommendations = (formData, waterResult) => {
  const recommendations = [];

  // Indoor recommendations
  if (formData.indoor?.bathingMethod === "shower") {
    if (formData.indoor?.showerDuration > 5) {
      recommendations.push({
        id: "shower",
        title: "Reduce Shower Time",
        description: "Cut shower time to 5 minutes or less",
        savingsLiters:
          (formData.indoor.showerDuration - 5) *
          (formData.indoor.efficientShowerhead ? 7.5 : 10) *
          (formData.indoor?.showerFrequency || 7) *
          52,
        cost: "Free",
      });
    }

    if (!formData.indoor?.efficientShowerhead) {
      recommendations.push({
        id: "efficientShowerhead",
        title: "Install Water-Efficient Showerhead",
        description:
          "Switch to a water-efficient showerhead to save 2.5 liters per minute",
        savingsLiters:
          2.5 *
          (formData.indoor?.showerDuration || 8) *
          (formData.indoor?.showerFrequency || 7) *
          52,
        cost: "₹500-1500",
      });
    }
  }

  // Bathtub recommendations
  if (formData.indoor?.bathingMethod === "bathtub") {
    recommendations.push({
      id: "reduceBaths",
      title: "Reduce Bathtub Usage",
      description: "Consider using showers instead of bathtub when possible",
      savingsLiters: 300 * (formData.indoor?.bathtubFrequency || 4) * 6, // Assuming half the baths are replaced with 5-min showers
      cost: "Free",
    });
  }

  // Toilet recommendations
  if (
    formData.indoor?.toiletType === "traditional" &&
    !formData.indoor?.usesTreatedWater
  ) {
    recommendations.push({
      id: "dualFlush",
      title: "Install Dual-Flush Toilet",
      description: "Replace traditional toilets with dual-flush models",
      savingsLiters: (10 - 4.5) * (formData.indoor?.toiletFlushes || 5) * 365, // Average 4.5L for dual flush
      cost: "₹5000-15000",
    });
  }

  // Faucet recommendations
  if (!formData.indoor?.efficientFaucets) {
    recommendations.push({
      id: "efficientFaucets",
      title: "Install Faucet Aerators",
      description: "Add aerators to your faucets to reduce water flow",
      savingsLiters: 2 * (formData.indoor?.faucetMinutes || 10) * 365,
      cost: "₹200-500",
    });
  }

  // Laundry recommendations
  if (
    formData.indoor?.laundryMethod === "machine" &&
    (formData.indoor?.washerType === "topLoad" ||
      formData.indoor?.washerType === "semiAuto")
  ) {
    recommendations.push({
      id: "efficientWasher",
      title: "Consider Front-Loading Washing Machine",
      description:
        "When replacing your washing machine, choose a front-loading model",
      savingsLiters:
        ((formData.indoor?.washerType === "topLoad" ? 140 : 120) - 60) *
        (formData.indoor?.laundryLoads || 3) *
        52,
      cost: "₹25000-40000",
    });
  }

  if (
    formData.indoor?.laundryMethod === "machine" &&
    !formData.indoor?.fullLoads
  ) {
    recommendations.push({
      id: "fullLoads",
      title: "Run Full Loads of Laundry",
      description:
        "Wait until you have a full load of laundry to run your washing machine",
      savingsLiters:
        0.2 *
        (formData.indoor?.laundryLoads || 3) *
        (formData.indoor?.washerType === "frontLoad"
          ? 60
          : formData.indoor?.washerType === "topLoad"
          ? 140
          : 120) *
        52,
      cost: "Free",
    });
  }

  // Outdoor recommendations
  if (
    formData.outdoor?.hasGarden &&
    formData.outdoor?.gardenWateringMethod === "hose"
  ) {
    recommendations.push({
      id: "drip",
      title: "Install Drip Irrigation",
      description:
        "Replace hose watering with water-efficient drip irrigation system",
      savingsLiters:
        (formData.outdoor?.gardenSqFt || 100) *
        0.25 *
        (formData.outdoor?.gardenWateringFrequency || 3) *
        30,
      cost: "₹1500-5000",
    });
  }

  if (
    formData.outdoor?.hasLawn &&
    formData.outdoor?.lawnWateringTime === "midday"
  ) {
    recommendations.push({
      id: "lawnWateringTime",
      title: "Water Lawn in Morning",
      description: "Water your lawn in early morning to reduce evaporation",
      savingsLiters:
        (formData.outdoor?.lawnSqFt || 1000) *
        0.5 *
        (formData.outdoor?.lawnWateringFrequency || 2) *
        0.4 *
        30,
      cost: "Free",
    });
  }

  if (formData.outdoor?.carWashMethod === "homeHose") {
    recommendations.push({
      id: "carWashBucket",
      title: "Use Bucket for Car Washing",
      description: "Use a bucket instead of a running hose to wash your car",
      savingsLiters:
        (400 - 40) * (formData.outdoor?.carWashFrequency || 1) * 12,
      cost: "Free",
    });
  }

  // Rain water harvesting recommendation
  if (
    !formData.outdoor?.collectsRainwater &&
    (formData.outdoor?.hasGarden || formData.outdoor?.hasLawn)
  ) {
    recommendations.push({
      id: "rainwater",
      title: "Harvest Rainwater",
      description: "Install a rainwater harvesting system for outdoor watering",
      savingsLiters:
        (formData.outdoor?.hasGarden
          ? (formData.outdoor?.gardenSqFt || 100) * 0.3 * 30
          : 0) +
        (formData.outdoor?.hasLawn
          ? (formData.outdoor?.lawnSqFt || 1000) * 0.2 * 30
          : 0),
      cost: "₹3000-15000",
    });
  }

  return recommendations;
};

export const calculateSavings = (
  currentFootprint,
  selectedRecommendations,
  recommendationsList
) => {
  const selectedRecs = recommendationsList.filter((rec) =>
    selectedRecommendations.includes(rec.id)
  );
  const totalSavings = selectedRecs.reduce(
    (sum, rec) => sum + rec.savingsLiters,
    0
  );

  return {
    savings: {
      annualLiters: totalSavings,
      percentReduction:
        (totalSavings / currentFootprint.totalAnnualLiters) * 100,
    },
    newFootprint: {
      totalAnnualLiters: currentFootprint.totalAnnualLiters - totalSavings,
      totalDailyLitersPerPerson:
        currentFootprint.totalDailyLitersPerPerson -
        totalSavings / 365 / (currentFootprint.householdSize || 1),
    },
  };
};
